import { useState, useEffect } from "react";
import { useSearch, useLocation } from "wouter";
import { Search, MapPin, SlidersHorizontal, LayoutGrid, List, Loader2, SearchX, X } from "lucide-react";
import { Tabs } from "../components/ui/kit";
import { PersonCard, ServiceCard, CompanyCard, JobCard, ProjectCard, RfqCard, ItemCard } from "../components/cards";
import { people as mockPeople, services as mockServices, companies as mockCompanies, jobs as mockJobs, projects as mockProjects, rfqs as mockRfqs } from "../data/mock";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const TABS = ["People", "Services", "Companies", "Jobs", "Projects", "RFQs", "Materials"];
const KIND: Record<string, "people" | "services" | "companies" | "jobs" | "projects" | "rfqs"> = {
  People: "people", Services: "services", Companies: "companies", Jobs: "jobs", Projects: "projects", RFQs: "rfqs",
};
// server returns from_price → card expects `from`
const mapService = (s: any) => ({ ...s, from: s.fromPrice ?? s.from ?? 0 });
const MOCK: Record<string, any[]> = {
  People: mockPeople, Services: mockServices.map(mapService), Companies: mockCompanies, Jobs: mockJobs, Projects: mockProjects, RFQs: mockRfqs, Materials: [],
};

const emptyFilters = { q: "", city: "", category: "", minRating: 0, verified: false, sort: "" };
const hasRating = (tab: string) => tab === "People" || tab === "Services" || tab === "Companies";

export default function Marketplace() {
  const search = useSearch();
  const [, navigate] = useLocation();
  const params0 = new URLSearchParams(search);
  const initial = params0.get("tab") || "People";
  const startTab = TABS.includes(initial) ? initial : "People";
  const { user } = useAuth();
  const [tab, setTab] = useState(startTab);
  // QA #22: job ids the user already applied to, so cards can show "Applied".
  const [appliedJobs, setAppliedJobs] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!user) { setAppliedJobs(new Set()); return; }
    api.myAppliedJobIds().then((ids) => setAppliedJobs(new Set(ids))).catch(() => {});
  }, [user]);
  // Seed filters from the URL so a search coming from the home page (or a shared
  // link) shows results immediately.
  const [f, setF] = useState({
    ...emptyFilters,
    q: params0.get("q") || "",
    city: params0.get("city") || "",
    category: params0.get("category") || "",
  });
  // view.tab and view.items always belong together, so a card never receives a
  // mismatched record while a tab switch is mid-flight.
  const [view, setView] = useState<{ tab: string; items: any[] }>({ tab: startTab, items: MOCK[startTab] });
  const [loading, setLoading] = useState(true);
  const [fallback, setFallback] = useState(false);
  const [layout, setLayout] = useState<"grid" | "list">("grid");
  const [showFilters, setShowFilters] = useState(false); // mobile filter bottom sheet

  const set = (patch: Partial<typeof emptyFilters>) => setF((prev) => ({ ...prev, ...patch }));

  // Keep the view in sync with the URL query (header nav + home search).
  // "Explore" points at /marketplace with no ?tab=, so a missing tab resets to
  // the default rather than leaving the previous tab stuck (fixes Explore no-op).
  useEffect(() => {
    const p = new URLSearchParams(search);
    const t = p.get("tab");
    setTab(t && TABS.includes(t) ? t : "People");
    // Only seed filters that are actually present in the URL, so typing in the
    // filter box (which doesn't touch the URL) is never clobbered.
    const patch: Partial<typeof emptyFilters> = {};
    if (p.has("q")) patch.q = p.get("q") || "";
    if (p.has("city")) patch.city = p.get("city") || "";
    if (p.has("category")) patch.category = p.get("category") || "";
    if (Object.keys(patch).length) setF((prev) => ({ ...prev, ...patch }));
  }, [search]);

  // debounced server-side fetch for the active tab + filters
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = {
      q: f.q || undefined,
      city: f.city || undefined,
      category: f.category || undefined,
      minRating: f.minRating || undefined,
      verified: f.verified || undefined,
      sort: f.sort || undefined,
    };
    const h = setTimeout(() => {
      const fetcher = tab === "Materials"
        ? api.listItems({ q: f.q || undefined, category: f.category || undefined })
        : api.list(KIND[tab], params);
      fetcher
        .then((list) => {
          if (!alive) return;
          setView({ tab, items: tab === "Services" ? list.map(mapService) : list });
          setFallback(false);
        })
        .catch(() => {
          if (!alive) return;
          setView({ tab, items: MOCK[tab] }); // network error → show seeded data unfiltered
          setFallback(true);
        })
        .finally(() => { if (alive) setLoading(false); });
    }, 300);
    return () => { alive = false; clearTimeout(h); };
  }, [tab, f.q, f.city, f.category, f.minRating, f.verified, f.sort]);

  const items = view.items;
  const count = items.length;
  const filtersActive = f.q || f.city || f.category || f.minRating > 0 || f.verified;
  // count of sheet filters active (keywords live in the search box, shown separately)
  const activeCount = [f.city, f.category, f.minRating > 0, f.verified].filter(Boolean).length;

  return (
    <div className="container-x py-6">
      <div className="mb-5"><Tabs tabs={TABS} active={tab} onChange={(t) => { setTab(t); navigate(`/marketplace?tab=${t}`); }} /></div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        {/* FILTERS */}
        <aside className="card p-4 h-fit hidden lg:block">
          <div className="flex items-center gap-2 mb-4"><SlidersHorizontal className="w-4 h-4 text-navy" /><span className="font-bold text-navy">Refine your search</span></div>
          <div className="space-y-4">
            <div><label className="label">Keywords</label>
              <input className="field" value={f.q} onChange={(e) => set({ q: e.target.value })} placeholder="Search keywords…" />
            </div>
            <div><label className="label">Location</label>
              <div className="relative"><MapPin className="w-4 h-4 absolute left-3 top-3.5 text-mut" /><input className="field pl-9" value={f.city} onChange={(e) => set({ city: e.target.value })} placeholder="City or Province" /></div>
            </div>
            <div><label className="label">Category</label>
              <select className="field" value={f.category} onChange={(e) => set({ category: e.target.value })}>
                <option value="">All Categories</option>
                <option>Construction</option><option>Professional</option><option>IT</option>
                <option>Electrical</option><option>Plumbing</option><option>Supply</option>
              </select>
            </div>
            <div className={hasRating(tab) ? "" : "opacity-50 pointer-events-none"}><label className="label">Rating</label>
              <div className="flex gap-1.5">
                {[["All", 0], ["4★+", 4], ["3★+", 3]].map(([lbl, val]) => (
                  <button key={lbl as string} onClick={() => set({ minRating: val as number })}
                    className={`chip ${f.minRating === val ? "bg-navy text-white" : "bg-navy-50 text-navy-600 hover:bg-navy hover:text-white"}`}>{lbl}</button>
                ))}
              </div>
            </div>
            <div><label className="label">Verification</label>
              <label className="flex items-center gap-2 text-sm text-mut"><input type="checkbox" checked={f.verified} onChange={(e) => set({ verified: e.target.checked })} /> Verified only</label>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setF({ ...emptyFilters })} className="btn-outline flex-1 h-9 text-xs">Clear</button>
            </div>
          </div>
        </aside>

        {/* RESULTS */}
        <div>
          {/* Mobile toolbar: search + Filters (opens bottom sheet) + Sort */}
          <div className="lg:hidden mb-4 space-y-2">
            <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3.5 text-mut" /><input className="field pl-9" value={f.q} onChange={(e) => set({ q: e.target.value })} placeholder="Search…" /></div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowFilters(true)} className="btn-outline h-10 flex-1">
                <SlidersHorizontal className="w-4 h-4" /> Filters
                {activeCount > 0 && <span className="ml-1 inline-grid place-items-center min-w-5 h-5 px-1 rounded-full bg-brand-red text-white text-[11px] font-bold">{activeCount}</span>}
              </button>
              <select className="field h-10 flex-1 text-sm" value={f.sort} onChange={(e) => set({ sort: e.target.value })}>
                <option value="">Most Relevant</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest</option>
                {(tab === "Services" || tab === "Projects") && <option value="price">Lowest Price</option>}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-mut flex items-center gap-2">
              {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Showing <b className="text-navy">{count.toLocaleString()}</b> {count === 1 ? "result" : "results"}{filtersActive ? " (filtered)" : ""}
            </p>
            <div className="hidden lg:flex items-center gap-3">
              <select className="field h-9 w-40 text-xs" value={f.sort} onChange={(e) => set({ sort: e.target.value })}>
                <option value="">Most Relevant</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest</option>
                {(tab === "Services" || tab === "Projects") && <option value="price">Lowest Price</option>}
              </select>
              <div className="flex border border-line rounded-lg overflow-hidden">
                <button onClick={() => setLayout("grid")} aria-label="Grid view" title="Grid view" className={`h-9 w-9 grid place-items-center ${layout === "grid" ? "bg-navy text-white" : "text-mut hover:text-navy"}`}><LayoutGrid className="w-4 h-4" /></button>
                <button onClick={() => setLayout("list")} aria-label="List view" title="List view" className={`h-9 w-9 grid place-items-center ${layout === "list" ? "bg-navy text-white" : "text-mut hover:text-navy"}`}><List className="w-4 h-4" /></button>
              </div>
            </div>
          </div>

          {fallback && <div className="mb-3 text-xs text-mut">Showing sample listings — live search is momentarily unavailable.</div>}

          {count === 0 && !loading ? (
            <div className="card p-10 text-center">
              <SearchX className="w-8 h-8 text-mut mx-auto mb-2" />
              {filtersActive ? (
                <>
                  <div className="font-bold text-navy">No {tab.toLowerCase()} match your filters</div>
                  <p className="text-sm text-mut mt-1">Try broadening your keywords or location.</p>
                  <button onClick={() => setF({ ...emptyFilters })} className="btn-outline mt-4 inline-flex">Clear filters</button>
                </>
              ) : (
                <>
                  <div className="font-bold text-navy">No {tab.toLowerCase()} listed here yet</div>
                  <p className="text-sm text-mut mt-1">
                    {tab === "People"
                      ? "Verified workers appear here once their identity is confirmed. In the meantime, browse other categories:"
                      : "Check back soon, or browse another category:"}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center mt-4">
                    {["Services", "Companies", "Jobs", "Projects"].filter((t) => t !== tab).map((t) => (
                      <button key={t} onClick={() => { setTab(t); navigate(`/marketplace?tab=${t}`); }} className="btn-outline h-9 px-3 text-sm">{t}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className={layout === "list" ? "grid gap-4 grid-cols-1" : "grid gap-4 sm:grid-cols-2 xl:grid-cols-3"}>
              {view.tab === "People" && items.map((p) => <PersonCard key={p.id} p={p} />)}
              {view.tab === "Services" && items.map((s) => <ServiceCard key={s.id} s={s} />)}
              {view.tab === "Companies" && items.map((c) => <CompanyCard key={c.id} c={c} />)}
              {view.tab === "Jobs" && items.map((j) => <JobCard key={j.id} j={j} applied={appliedJobs.has(j.id)} />)}
              {view.tab === "Projects" && items.map((p) => <ProjectCard key={p.id} p={p} />)}
              {view.tab === "RFQs" && items.map((r) => <RfqCard key={r.id} r={r} />)}
              {view.tab === "Materials" && items.map((it) => <ItemCard key={it.id} it={it} />)}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <span className="font-bold text-navy flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Filters</span>
              <button onClick={() => setShowFilters(false)} aria-label="Close" className="btn-ghost h-9 w-9 p-0"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4 overflow-auto">
              <div><label className="label">Location</label>
                <div className="relative"><MapPin className="w-4 h-4 absolute left-3 top-3.5 text-mut" /><input className="field pl-9" value={f.city} onChange={(e) => set({ city: e.target.value })} placeholder="City or Province" /></div>
              </div>
              <div><label className="label">Category</label>
                <select className="field" value={f.category} onChange={(e) => set({ category: e.target.value })}>
                  <option value="">All Categories</option>
                  <option>Construction</option><option>Professional</option><option>IT</option>
                  <option>Electrical</option><option>Plumbing</option><option>Supply</option>
                </select>
              </div>
              <div className={hasRating(tab) ? "" : "opacity-50 pointer-events-none"}><label className="label">Rating</label>
                <div className="flex gap-2">
                  {[["All", 0], ["4★+", 4], ["3★+", 3]].map(([lbl, val]) => (
                    <button key={lbl as string} onClick={() => set({ minRating: val as number })}
                      className={`chip px-4 py-2 ${f.minRating === val ? "bg-navy text-white" : "bg-navy-50 text-navy-600"}`}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div><label className="label">Verification</label>
                <label className="flex items-center gap-2 text-sm text-mut"><input type="checkbox" checked={f.verified} onChange={(e) => set({ verified: e.target.checked })} /> Verified only</label>
              </div>
            </div>
            <div className="flex gap-2 p-4 border-t border-line pb-[calc(env(safe-area-inset-bottom)+16px)]">
              <button onClick={() => setF({ ...emptyFilters })} className="btn-outline flex-1">Clear</button>
              <button onClick={() => setShowFilters(false)} className="btn-red flex-1">Show {count.toLocaleString()} results</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
